import React, { useState, useEffect } from 'react';
import './ParentDashboard.css';
import DashboardHeader from '../components/DashboardHeader';
import ParentNavigationBar from '../components/ParentNavigationBar';
import { BellIcon, AlertIcon, CalendarIcon, BookIcon, InfoIcon, ClockIcon, ChartIcon, CheckCircleIcon, CreditCardIcon, MailIcon } from '../components/Icons';
import { toSentenceCase } from '../utils/textUtils';

function ParentDashboard() {
  const [selectedTrimester, setSelectedTrimester] = useState('1');
  const [studentInfo, setStudentInfo] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState({
    absences: 0,
    retards: 0,
    moyenne: 0,
    notes_faibles: 0
  });
  const [loading, setLoading] = useState(true);

  const [bulletins, setBulletins] = useState([]);
  const [showBulletinModal, setShowBulletinModal] = useState(false);

  useEffect(() => {
    fetchStudentData();
    fetchNotifications();
    fetchStats();
    fetchBulletins();
  }, [selectedTrimester]);

  const fetchBulletins = async () => {
    try {
      if (studentInfo?.id) {
        const response = await fetch(`http://127.0.0.1:8000/api/v1/bulletins/etudiant/${studentInfo.id}`);
        const data = await response.json();
        
        if (data.success) {
          setBulletins(data.data || []);
        }
      }
    } catch (error) {
      console.error('Erreur:', error);
    }
  };

  const telechargerBulletin = async (bulletinId, trimestre) => {
    try {
      const response = await fetch(`http://127.0.0.1:8000/api/v1/bulletins/${bulletinId}/telecharger`);
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bulletin_trimestre_${trimestre}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        alert(toSentenceCase('Bulletin téléchargé avec succès !'));
        fetchBulletins(); // Actualiser la liste
      } else {
        alert(toSentenceCase('Erreur lors du téléchargement du bulletin'));
      }
    } catch (error) {
      console.error('Erreur:', error);
      alert(toSentenceCase('Erreur lors du téléchargement'));
    }
  };

  const ouvrirModalBulletin = () => {
    if (studentInfo?.id) {
      fetchBulletins();
    }
    setShowBulletinModal(true);
  };

  const fetchStudentData = async () => {
    try {
      setLoading(true);
      const userEmail = localStorage.getItem('user_email') || 'parent@example.com';
      const response = await fetch(`http://127.0.0.1:8000/api/v1/etudiant/me?email=${userEmail}`);
      const data = await response.json();
      
      if (response.ok && data.etudiant) {
        setStudentInfo(data.etudiant);
      }
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    try {
      // TODO: Implémenter l'API de notifications réelles
      // Pour l'instant, aucune notification
      setNotifications([]);
    } catch (error) {
      console.error('Erreur:', error);
      setNotifications([]);
    }
  };

  const fetchStats = async () => {
    try {
      // TODO: Implémenter l'API de statistiques réelles
      // Pour l'instant, stats à zéro
      setStats({
        absences: 0,
        retards: 0,
        moyenne: 0,
        notes_faibles: 0
      });
    } catch (error) {
      console.error('Erreur:', error);
      setStats({
        absences: 0,
        retards: 0,
        moyenne: 0,
        notes_faibles: 0
      });
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'absence': return '🚫';
      case 'retard': return '⏰';
      case 'note': return '📉';
      case 'convocation': return '📞';
      case 'paiement': return '💰';
      case 'bulletin': return '📄';
      default: return '📢';
    }
  };

  const getNotificationColor = (priority) => {
    switch (priority) {
      case 'critical': return '#DC3545';
      case 'high': return '#FF6B35';
      case 'medium': return '#F39C12';
      case 'low': return '#3498DB';
      default: return '#6C757D';
    }
  };

  const getPriorityLabel = (priority) => {
    switch (priority) {
      case 'critical': return 'URGENT';
      case 'high': return 'Important';
      case 'medium': return 'Moyen';
      case 'low': return 'Info';
      default: return '';
    }
  };

  const markAsRead = (notificationId) => {
    setNotifications(prev => 
      prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
    );
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  const userType = localStorage.getItem('user_type') || 'parent';
  const isParent = userType === 'parent';

  if (loading) {
    return (
      <div className="admin-layout">
        <DashboardHeader />
        <ParentNavigationBar 
          title={isParent ? "Suivi de mon Enfant" : "Mon Tableau de Bord"} 
          showBack={false}
        />
        <div className="admin-container">
          <main className="parent-main admin-content-full">
            <div className="loading-state">
              <div className="spinner"></div>
              <p>{toSentenceCase("Chargement des informations...")}</p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-layout">
      <DashboardHeader />
      <ParentNavigationBar 
        title="Suivi Scolaire" 
        showBack={false}
      />
      <div className="admin-container">
        <main className="parent-main admin-content-full">
          <div className="parent-dashboard">
            
            {/* Bannière de suivi - Espace Parent d'Élève */}
            <div className="portal-hero-banner">
              <div className="portal-hero-main">
                <span className="portal-hero-label">Portail Parent d'Élève</span>
                
                <h1 className="portal-hero-title">
                  Suivi Scolaire de <span className="portal-hero-name">{studentInfo?.prenom} {studentInfo?.nom}</span>
                </h1>

                <p className="portal-hero-subtitle">
                  Classe de <strong className="portal-hero-highlight">{studentInfo?.classe || '6ème A'}</strong> • Année Scolaire 2025-2026
                </p>

                <div className="portal-hero-date">
                  <CalendarIcon size={14} color="#94a3b8" /> {new Date().toLocaleDateString('fr-FR', { 
                    weekday: 'long', 
                    day: 'numeric', 
                    month: 'long', 
                    year: 'numeric' 
                  })}
                </div>
              </div>

              <div className="portal-hero-actions">
                <div className="portal-avatar-circle">
                  {((studentInfo?.prenom?.[0] || 'E') + (studentInfo?.nom?.[0] || 'L')).toUpperCase()}
                </div>
                <div className="trimester-select-wrapper">
                  <label htmlFor="parent-trimester-select">{toSentenceCase("Trimestre")}:</label>
                  <select 
                    id="parent-trimester-select"
                    value={selectedTrimester} 
                    onChange={(e) => setSelectedTrimester(e.target.value)}
                  >
                    <option value="1">{toSentenceCase("Trimestre 1")}</option>
                    <option value="2">{toSentenceCase("Trimestre 2")}</option>
                    <option value="3">{toSentenceCase("Trimestre 3")}</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Statistiques rapides */}
            <div className="stats-overview">
              <div className="stat-card-parent" style={{ borderLeftColor: '#DC3545' }}>
                <div className="stat-icon" style={{ backgroundColor: '#DC3545' }}>
                  <CalendarIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.absences}</h3>
                  <p>{toSentenceCase("Absences")}</p>
                </div>
              </div>
              <div className="stat-card-parent" style={{ borderLeftColor: '#F39C12' }}>
                <div className="stat-icon" style={{ backgroundColor: '#F39C12' }}>
                  <ClockIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.retards}</h3>
                  <p>{toSentenceCase("Retards")}</p>
                </div>
              </div>
              <div className="stat-card-parent" style={{ borderLeftColor: stats.moyenne >= 10 ? '#27AE60' : '#DC3545' }}>
                <div className="stat-icon" style={{ backgroundColor: stats.moyenne >= 10 ? '#27AE60' : '#DC3545' }}>
                  <ChartIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.moyenne}/20</h3>
                  <p>{toSentenceCase("Moyenne générale")}</p>
                </div>
              </div>
              <div className="stat-card-parent" style={{ borderLeftColor: '#FF6B35' }}>
                <div className="stat-icon" style={{ backgroundColor: '#FF6B35' }}>
                  <AlertIcon size={24} color="#fff" />
                </div>
                <div className="stat-content">
                  <h3>{stats.notes_faibles}</h3>
                  <p>{toSentenceCase("Notes faibles")}</p>
                </div>
              </div>
            </div>

            {/* Section Notifications */}
            <div className="notifications-section-parent">
              <div className="section-header-parent">
                <h3>
                  <BellIcon size={24} color="#0078D7" />
                  {toSentenceCase("Notifications du trimestre")} {selectedTrimester}
                </h3>
                {unreadCount > 0 && (
                  <span className="unread-badge">{unreadCount} {toSentenceCase("non lue")}{unreadCount > 1 ? 's' : ''}</span>
                )}
              </div>

              {notifications.length === 0 ? (
                <div className="empty-notifications">
                  <div style={{ marginBottom: '16px' }}>
                    <CheckCircleIcon size={48} color="#27AE60" />
                  </div>
                  <p>{toSentenceCase("Aucune notification pour ce trimestre")}</p>
                  <span>{toSentenceCase("Tout est à jour")}</span>
                </div>
              ) : (
                <div className="notifications-list-parent">
                  {notifications.map(notification => (
                    <div 
                      key={notification.id} 
                      className={`notification-card ${notification.read ? 'read' : 'unread'}`}
                      onClick={() => markAsRead(notification.id)}
                    >
                      <div className="notification-icon-wrapper" style={{ backgroundColor: getNotificationColor(notification.priority) + '20' }}>
                        <BellIcon size={20} color={getNotificationColor(notification.priority)} />
                      </div>
                      <div className="notification-content">
                        <div className="notification-header-row">
                          <h4>{notification.title}</h4>
                          <span 
                            className="priority-badge" 
                            style={{ backgroundColor: getNotificationColor(notification.priority) }}
                          >
                            {getPriorityLabel(notification.priority)}
                          </span>
                        </div>
                        <p className="notification-message">{notification.message}</p>
                        <span className="notification-date">
                          <CalendarIcon size={14} />
                          {new Date(notification.date).toLocaleDateString('fr-FR', { 
                            day: 'numeric', 
                            month: 'long', 
                            year: 'numeric' 
                          })}
                        </span>
                      </div>
                      {!notification.read && <div className="unread-indicator"></div>}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions Rapides Métier */}
            <div className="quick-actions-parent">
              <h3 style={{ fontSize: '1.2rem', fontWeight: 800, color: '#0f172a', marginBottom: '1.2rem' }}>Accès Rapides & Services</h3>
              <div className="actions-grid">
                <button className="action-card" onClick={ouvrirModalBulletin}>
                  <BookIcon size={32} color="#27AE60" />
                  <span>{toSentenceCase("Bulletins PDF")}</span>
                </button>
                <button className="action-card" onClick={() => window.navigateTo && window.navigateTo('student-notes')}>
                  <ChartIcon size={32} color="#0078D7" />
                  <span>{toSentenceCase("Relevé de Notes")}</span>
                </button>
                <button className="action-card" onClick={() => window.navigateTo && window.navigateTo('payement')}>
                  <CreditCardIcon size={32} color="#9C27B0" />
                  <span>{toSentenceCase("Payer scolarité")}</span>
                </button>
                <button className="action-card" onClick={() => window.navigateTo && window.navigateTo('contact-lycee')}>
                  <MailIcon size={32} color="#F39C12" />
                  <span>{toSentenceCase("Contacter l'école")}</span>
                </button>
              </div>
            </div>

          </div>
        </main>
      </div>

      {/* Modal de téléchargement de bulletin */}
      {showBulletinModal && (
        <div className="modal-overlay">
          <div className="modal">
            <div className="modal-header">
              <h3>{toSentenceCase("Télécharger un bulletin")}</h3>
              <button 
                className="modal-close"
                onClick={() => setShowBulletinModal(false)}
              >
                ×
              </button>
            </div>
            <div className="modal-content">
              {bulletins.length === 0 ? (
                <div className="empty-bulletins">
                  <BookIcon size={48} color="#ccc" />
                  <p>{toSentenceCase("Aucun bulletin disponible")}</p>
                  <small>{toSentenceCase("Les bulletins seront disponibles après validation des notes par les professeurs")}</small>
                </div>
              ) : (
                <div className="bulletins-list">
                  {bulletins.map((bulletin) => (
                    <div key={bulletin.id} className="bulletin-item">
                      <div className="bulletin-info">
                        <h4>{toSentenceCase("Trimestre")} {bulletin.trimestre}</h4>
                        <p>{toSentenceCase("Année scolaire")}: {bulletin.annee_scolaire}</p>
                        {bulletin.moyenne_generale && (
                          <p>{toSentenceCase("Moyenne")}: <strong>{bulletin.moyenne_generale}/20</strong></p>
                        )}
                        {bulletin.rang && (
                          <p>{toSentenceCase("Rang")}: <strong>{bulletin.rang}{bulletin.rang === 1 ? 'er' : 'ème'}</strong></p>
                        )}
                        <small>{toSentenceCase("Généré le")} {new Date(bulletin.date_generation).toLocaleDateString('fr-FR')}</small>
                      </div>
                      <div className="bulletin-actions">
                        {bulletin.peut_telecharger ? (
                          <button 
                            className="btn btn-primary btn-sm"
                            onClick={() => telechargerBulletin(bulletin.id, bulletin.trimestre)}
                          >
                            <BookIcon size={16} />
                            {toSentenceCase("Télécharger")}
                          </button>
                        ) : (
                          <span className="status-badge badge-warning">{toSentenceCase("En cours")}</span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ParentDashboard;
